import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { useGetDemoSuffix } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { selectExperiencesAudiencesReportFilter } from '@bbkAdminRedux/rtkq/app-persist/reports-filter';
import {
  type GetReportV2Args,
  type LifecycleCustomerReport,
  type ReportFilter,
  reportsSlice,
  ReportType,
  useExportCustomersData,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { selectFromResultTyped } from '@bbkAdminRedux/redux_utils';
import {
  SButton,
  SIcon,
  SPopover,
  STable,
  STooltip,
} from '@chargebee/sting-react';
import { formatCurrencyOrNumber } from '@bbkAdminComponents/predictive/customer-risk-level-widget';
import moment from 'moment/moment';
import { EngagedStatusPill } from '@bbkAdminComponents/play/engaged-status-pill';
import { capitalize, toUpper } from 'lodash';
import { FulfillmentStatusPill } from '@bbkAdminComponents/play/fulfillment-status-pill';
import { humanizeString } from '@bbkAdminUtils/utils';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { type LCOReportOpts } from '@bbkAdminUtils/api-client/reports-api';

type Props = {
  playId: string;
  offerId: string;
  totalCustomersCount: number;
};

export const CustomerSnapshotTableV2: React.FC<Props> = ({
  playId,
  offerId,
  totalCustomersCount,
}) => {
  const appKey = useSelector(selectAppId);
  const companyId = useSelector(selectCompanyInternalName);

  const isDemoData = useShowPlayDemoData();
  const { startDate: start, endDate: end } = useSelectDatePicker();
  const suffix = useGetDemoSuffix();
  const reportFilters = useSelector(
    selectExperiencesAudiencesReportFilter(suffix)
  );

  const detailFilters: ReportFilter[] = [
    ...reportFilters,
    { field: 'play_id', operator: 'equals', value: playId },
    {
      field: 'offer_id',
      operator: 'equals',
      value: offerId,
    },
    //TODO: see if we can deprecate this field soon
    {
      field: 'target_type',
      operator: 'equals',
      value: 'OFFER',
    },
  ];

  const detailRequest = {
    report_type: ReportType.lifecycle_entity_metrics_customer_snapshot,
    parameters: {
      group_by_period: 'all',
    },
    filters: [detailFilters],
    limit: 5,
  };

  const detailArgs: GetReportV2Args = {
    appKey,
    start,
    end,
    report_type_requests: [detailRequest],
    context: isDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  };

  const detailOpts = {
    skip: !appKey || !start || !end || !offerId,
    selectFromResult: selectFromResultTyped<LifecycleCustomerReport>,
  };

  const {
    data: customerSnapshot,
    isFetching: isSnapshotFetching,
    isLoading: isSnapshotLoading,
  } = reportsSlice.endpoints.getReportV2.useQuery(detailArgs, detailOpts);
  const snapshotData = customerSnapshot?.customers;
  const hasData = snapshotData && snapshotData.length > 0;
  const exportLCOReportingData = useExportCustomersData();
  const [openExportMenu, setOpenExportMenu] = useState<boolean>(false);

  const exportData = async (reportType: ReportType) => {
    setOpenExportMenu(false);
    const lcoReportOpts: LCOReportOpts = {
      start,
      end,
      format: 'CSV',
      reportType,
      filters: [
        [
          {
            field: 'play_id',
            operator: 'equals',
            value: playId,
          },
          {
            field: 'offer_id',
            operator: 'equals',
            value: offerId,
          },
          {
            field: 'target_type',
            operator: 'equals',
            value: 'OFFER',
          },
        ],
      ],
    };

    try {
      return await exportLCOReportingData(lcoReportOpts, {
        appId: appKey,
        companyId,
        returnDownloadUrl: false,
      });
    } catch (e) {
      console.error((e as any).message);
    }
  };

  const exportButton = (
    <SPopover open={openExportMenu} onOpenChange={setOpenExportMenu}>
      <SPopover.Trigger asChild>
        <SButton
          variant="primary-ghost"
          icon={<SIcon name="download" size={16} />}
          iconPosition="left"
          disabled={!hasData || isDemoData}
        >
          Export full list
        </SButton>
      </SPopover.Trigger>
      <SPopover.Content align="end" className="!tw-overflow-hidden !tw-p-0">
        <div className="tw-flex tw-max-w-80 tw-flex-col">
          <div className="!tw-bg-neutral-50 tw-px-4 tw-py-1">
            <div className="tw-text-lg tw-font-bold">Export report list</div>
          </div>
          <button
            onClick={() =>
              exportData(
                ReportType.lifecycle_entity_metrics_customer_snapshot_export
              )
            }
            className="tw-px-4 tw-py-2 tw-text-start hover:tw-bg-primary-50"
          >
            <div className="tw-font-semibold">Customer level report</div>
            <div className="tw-text-xs">
              List of all the customers who engaged with the offers in the play.
            </div>
          </button>
          <button
            onClick={() =>
              exportData(ReportType.lifecycle_entity_metrics_user_export)
            }
            className="tw-px-4 tw-py-2 tw-text-start hover:tw-bg-primary-50"
          >
            <div className="tw-font-semibold">User level report</div>
            <div className="tw-text-xs">
              List of all the users who engaged with offers along with their
              status.
            </div>
          </button>
        </div>
      </SPopover.Content>
    </SPopover>
  );

  const emptyRevenueTooltip = (
    <STooltip
      label="No revenue to display yet — there haven't been any fulfilled items for this offer."
      width="regular"
      placement="top"
    >
      <span className="tw-underline tw-decoration-dotted tw-underline-offset-2">
        --
      </span>
    </STooltip>
  );

  return (
    <div>
      <div className="tw-mb-4 tw-mt-6 tw-flex tw-items-center tw-gap-2">
        <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-100 tw-bg-primary-50 tw-p-2">
          <SIcon name="user-check" size={18} className="tw-text-primary-600" />
        </div>
        <span className="tw-text-lg tw-font-semibold tw-text-neutral-900">
          Engaged Subscriber Preview ({snapshotData?.length ?? 0} of{' '}
          {formatCurrencyOrNumber(totalCustomersCount)})
        </span>
      </div>
      <div className="tw-mb-4 tw-flex tw-flex-row tw-items-center tw-justify-between">
        <div className="tw-text-sm tw-font-light tw-text-neutral-600">
          To view the full list of customers and users who have engaged with the
          offer, export the CSV.
        </div>
        {hasData &&
          (isDemoData ? (
            <STooltip
              label="Disabled in demo mode."
              width="regular"
              placement="left"
            >
              <div>{exportButton}</div>
            </STooltip>
          ) : (
            exportButton
          ))}
      </div>
      {hasData || isSnapshotFetching || isSnapshotLoading ? (
        <div className="tw-mb-6 tw-w-full">
          <STable
            loading={isSnapshotFetching || isSnapshotLoading}
            loadingRows={5}
            loadingColumns={7}
          >
            <STable.Head>
              <STable.Row>
                <STable.HeaderCell className="!tw-text-xs">
                  Customer
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Customer ID
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Last engaged date
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Last engaged status
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Fulfillment status
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Revenue generated
                </STable.HeaderCell>
                <STable.HeaderCell className="!tw-text-xs">
                  Last engaged user
                </STable.HeaderCell>
              </STable.Row>
            </STable.Head>
            <STable.Body>
              {snapshotData &&
                snapshotData.map((customer) => (
                  <STable.Row>
                    <STable.Cell>{customer.customer_name}</STable.Cell>
                    <STable.Cell>{customer.customer_id}</STable.Cell>
                    <STable.Cell>
                      {moment(customer.last_engaged_date).format('MM/DD/YYYY')}
                    </STable.Cell>
                    <STable.Cell>
                      <span className="tw-flex tw-items-center">
                        <EngagedStatusPill
                          status={toUpper(customer.last_engaged_status)}
                        />
                        {capitalize(customer.last_engaged_status)}
                      </span>
                    </STable.Cell>
                    <>
                      <STable.Cell>
                        <span className="tw-flex tw-items-center">
                          {customer.fulfillment_status ? (
                            <>
                              <FulfillmentStatusPill
                                status={customer.fulfillment_status}
                              />
                              {humanizeString(customer.fulfillment_status)}
                            </>
                          ) : (
                            <TippyTooltip
                              content={
                                <span className="tw-font-normal">
                                  Fulfillment doesn&apos;t start until the offer
                                  has been accepted
                                </span>
                              }
                            >
                              <span className="tw-underline tw-decoration-dotted tw-underline-offset-2">
                                N/A
                              </span>
                            </TippyTooltip>
                          )}
                        </span>
                      </STable.Cell>
                      <STable.Cell>
                        {isDefined(customer.revenue)
                          ? formatCurrencyOrNumber(customer.revenue, true)
                          : emptyRevenueTooltip}
                      </STable.Cell>
                    </>
                    <STable.Cell>{customer.user_id}</STable.Cell>
                  </STable.Row>
                ))}
            </STable.Body>
          </STable>
        </div>
      ) : (
        <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-pb-8 tw-pt-4">
          <span className="tw-text-neutral-600">No snapshot data found</span>
        </div>
      )}
    </div>
  );
};
